import { AsyncPipe, NgClass } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { BreadcrumbItem } from '@scalefast/core';
import { BreadcrumbService, StaticFileHash } from '@scalefast/ecommerce-core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ContentfulSectionContent } from 'src/app/core/interfaces/Contentful/ContentfulSectionContent';
import { getClassForContentfulPath } from 'src/app/shared/helpers/contentful.helper';
import { ContentfulLayoutZoneBlockContent } from '../../../../core/interfaces/Contentful/LayoutZone/ContentfulLayoutZoneContent';
import { MetaTagsService } from '../../../services';
import { AppContentfulArticlesListComponent } from '../contentful-articles-list/contentful-articles-list.component';
import { AppContentfulContentDetailsBlockComponent } from '../contentful-content-details-block/contentful-content-details-block.component';
import { AppContentfulLandingPageSectionComponent } from '../contentful-landing-page-section/contentful-landing-page-section.component';

@Component({
  selector: 'app-contentful-landing-page',
  templateUrl: './contentful-landing-page.component.html',
  styleUrls: ['../../../../../scss/pages/contentful.scss', './contentful-landing-page.component.scss'],
  standalone: true,
  imports: [
    NgClass,
    AppContentfulLandingPageSectionComponent,
    AppContentfulContentDetailsBlockComponent,
    AppContentfulArticlesListComponent,
    AsyncPipe,
    StaticFileHash,
  ],
})
export class AppContentfulLandingPageComponent implements OnChanges {
  @Input() contentfulSectionContent: ContentfulSectionContent | undefined;
  contentfulCategories: Array<ContentfulLayoutZoneBlockContent> = [];
  breadcrumbs$: Observable<BreadcrumbItem[]>;
  articleBreadcrumbs: BreadcrumbItem | undefined;
  addClass: string = '';
  contentfulCategories$ = new BehaviorSubject<ContentfulLayoutZoneBlockContent[]>([]);

  constructor(
    private readonly breadcrumbService: BreadcrumbService,
    private router: Router,
    private metaService: MetaTagsService,
  ) {
    this.breadcrumbs$ = this.breadcrumbService.breadcrumbs$;

    this.addClass = getClassForContentfulPath(this.router.url);
  }

  ngOnChanges(changes: SimpleChanges): void {
    const currentValue = changes.contentfulSectionContent.currentValue;
    if (currentValue) {
      const contentfulSectionContentCategories = currentValue.contentfulContent.content;

      // We need to filter the list of categories to check if all have a list of valid and non-empty articles:
      const filteredContentfulSectionContentCategories = this.filterContentfulCategories(contentfulSectionContentCategories);

      const contentWithMeta = changes.contentfulSectionContent.currentValue.contentfulContent.content.find(
        (item: ContentfulLayoutZoneBlockContent) => item.meta,
      );

      if (contentWithMeta) {
        this.metaService.setMetaTags({
          description: contentWithMeta.meta.fields.metaDescription,
          title: contentWithMeta.meta.fields.metaTitle,
          keywords: contentWithMeta.meta.fields.metaKeywords,
        });
      }

      this.getCategorySlug(filteredContentfulSectionContentCategories);

      this.contentfulCategories$.next(filteredContentfulSectionContentCategories);
    }

    this.breadcrumbs$.subscribe((breadcrumbs) => {
      const breadcrumbsLength = breadcrumbs.length;

      if (breadcrumbs.length > 2) {
        this.articleBreadcrumbs = {
          label: breadcrumbs[breadcrumbsLength - 2].label,
          path: breadcrumbs[breadcrumbsLength - 2].path,
        };
      }
    });
  }

  trackByName(index: number, contentfulLayoutZoneBlockContent: ContentfulLayoutZoneBlockContent) {
    return contentfulLayoutZoneBlockContent.name;
  }

  /**
   Filter all the categories list
   */
  filterContentfulCategories(
    contentfulCategories: Array<ContentfulLayoutZoneBlockContent>,
  ): Array<ContentfulLayoutZoneBlockContent> {
    return contentfulCategories.filter((category) => {
      if (category.articleList && category.articleList.length === 0 && category.ctaLabel) {
        return false;
      }
      category.articleList = category.articleList?.filter((articleList) => !!articleList.overview?.name);
      return true;
    });
  }

  /**
   Iterate over all the categories list and get the first article slug removing the last / section
   */
  getCategorySlug(contentfulCategories: ContentfulLayoutZoneBlockContent[]): void {
    contentfulCategories.forEach((category) => {
      const firstArticle = category.articleList?.[0]; // Get the first article in the list if it exists
      const firstArticleSlug = firstArticle?.overview?.slug; // Get the slug of the first article if it exists

      if (firstArticleSlug) {
        // If the slug exists, update the category's categorySlug property
        category.categorySlug = firstArticleSlug.substring(0, firstArticleSlug.lastIndexOf('/'));
      }
    });
  }

  shouldShowCategory(contentfulCategory: ContentfulLayoutZoneBlockContent): boolean {
    return (
      !!(
        contentfulCategory.categoryHeading ||
        (!contentfulCategory.categoryHeading && contentfulCategory.description) ||
        contentfulCategory.videoUrl ||
        (contentfulCategory.contentType === 'contentBlock' && contentfulCategory.backgroundImageAsset)
      ) && contentfulCategory.contentType !== 'contentDetailsBlock'
    );
  }
}
